export type Lang = "en" | "es";

const en = {
  brand: {
    name: "R3stan Drywall, Inc.",
    short: "R3STAN",
    sub: "DRYWALL, INC.",
    r3: "R3 Services",
  },
  nav: {
    services: "Services",
    work: "Our Work",
    builders: "For Builders",
    r3: "R3 Services",
    about: "About",
    contact: "Contact",
    requestBid: "Request a Bid",
    stickyBid: "Request a Bid",
    faq: "FAQ",
    reviews: "Reviews",
    menu: "Menu",
    close: "Close",
  },
  hero: {
    eyebrow: "Drywall Contractor · Greater Houston, TX",
    line1: "DRYWALL &",
    line2: "CONSTRUCTION",
    line3: "DONE RIGHT.",
    tagline: "Residential · Commercial · New Construction",
    sub: "Insulation to final texture — one crew, one schedule, one standard of finish.",
    ctaBid: "Request a Bid",
    ctaPlans: "Send Plans / Request a Bid",
    ctaCall: "Call 281-541-8800",
    scroll: "Scroll to explore",
  },
  marquee: [
    "Residential",
    "Commercial",
    "New Construction",
    "Insulation",
    "Sheetrock",
    "Finishing",
    "Texture",
    "Painting",
  ],
  builders: {
    label: "For Builders & Contractors",
    heading: "A drywall partner that runs like part of your own crew.",
    intro:
      "We work the way builders need us to: clear answers, honest schedules, and finishes that pass inspection the first time. Show us the plans — we'll show you exactly how we'll hit them.",
    chapters: [
      {
        num: "01",
        title: "Communication",
        text: "One point of contact from estimate to punch list. You always know who is on site, what is next, and when it is done — in English or Spanish.",
      },
      {
        num: "02",
        title: "Scheduling",
        text: "We build our crews around your timeline, coordinate with your other trades, and flag conflicts early — before they cost you days.",
      },
      {
        num: "03",
        title: "Quality",
        text: "Straight walls, smooth finishes, consistent texture. We hold every board and every coat to the same standard, on every job, every time.",
      },
    ],
    cta: "Start a Conversation",
  },
  services: {
    label: "What We Do",
    heading: "Drywall services, end to end.",
    sub: "From the first layer of insulation to the final coat of paint.",
    items: {
      insulation: {
        title: "Insulation",
        text: "Properly fitted insulation that sets the stage for comfort and clean walls.",
      },
      sheetrock: {
        title: "Sheetrock / Drywall Installation",
        text: "Precise hanging, tight seams, and solid fastening on residential and commercial frames.",
      },
      finishing: {
        title: "Finishing",
        text: "Taping, floating, and sanding taken to the level your project calls for.",
      },
      texture: {
        title: "Texture",
        text: "Knockdown, orange peel, smooth — matched and applied evenly across every surface.",
      },
      painting: {
        title: "Painting",
        text: "Clean, complete interior paint that closes out the job ready for handover.",
      },
      residential: {
        title: "Residential Construction",
        text: "Single-family, multi-family, and remodels — full drywall scopes for home builders.",
      },
      commercial: {
        title: "Commercial Construction",
        text: "Tenant finish-outs and commercial build-outs delivered on schedule.",
      },
    } as Record<string, { title: string; text: string }>,
  },
  work: {
    label: "Our Work",
    heading: "Projects & finishes.",
    sub: "A look at the standard we bring to every job site.",
    note: "Placeholder photography — real project photos will replace these tiles.",
    all: "All",
    comingSoon: "Real project photo coming soon",
    cats: {
      sheetrock: "Sheetrock / Drywall",
      finishing: "Finishing",
      texture: "Texture",
      insulation: "Insulation",
      painting: "Painting",
      residential: "Residential",
      commercial: "Commercial",
      r3services: "R3 Services",
      dirtWork: "Dirt Work",
      cleanup: "Cleanup",
      demolition: "Demolition",
      hauling: "Hauling",
    } as Record<string, string>,
  },
  r3: {
    label: "A Division of R3stan",
    heading: "R3 Services",
    tagline: "Land. Cleanup. Hauling. Light Demolition.",
    sub: "The sister crew that gets your site ready — and keeps it clean.",
    items: {
      junk_removal: {
        title: "Junk Removal",
        text: "Debris and junk cleared off your property, fast.",
      },
      property_cleanup: {
        title: "Property Cleanup",
        text: "Full lot and property cleanups, ready for the next phase.",
      },
      concrete_breaking: {
        title: "Concrete Breaking",
        text: "Old concrete broken up and hauled out — sidewalks, slabs, and more.",
      },
      light_demolition: {
        title: "Light Demolition",
        text: "Interior tear-outs and light structures, done carefully.",
      },
      dirt_delivery: {
        title: "Dirt Delivery",
        text: "Fill dirt and material delivered straight to your site.",
      },
      dirt_work: {
        title: "Dirt Work",
        text: "Grading and dirt moving to get your project level and ready.",
      },
      tree_services: {
        title: "Tree Services",
        text: "Trimming and removal to clear and open up your land.",
      },
      trash_haul: {
        title: "Trash Haul Away",
        text: "Reliable hauling that gets trash and debris off your site.",
      },
    } as Record<string, { title: string; text: string }>,
    cta: "Request R3 Services",
    contactLabel: "R3 Services Contact",
    drywallNote: "Drywall & construction: Rogelio Tristan · 281-541-8800",
    equipment: {
      label: "Equipment & Projects",
      note: "Placeholder tiles — real R3 Services photos will be added here.",
      comingSoon: "Photo coming soon",
      items: {
        bobcat: "Bobcat / Skid-Steer Work",
        dirtWork: "Dirt Work",
        cleanup: "Property Cleanup",
        junk: "Junk & Trash Removal",
        concrete: "Concrete Breaking",
        demolition: "Light Demolition",
        hauling: "Hauling",
      } as Record<string, string>,
    },
  },
  about: {
    label: "About",
    heading: "A crew built on straight answers and straight walls.",
    text: "R3stan Drywall, Inc. is a Houston-area drywall and construction contractor serving builders, general contractors, and homeowners across Greater Houston. Every project runs through one person who answers the phone.",
    text2:
      "Call Rogelio directly — you will get a straight answer on scope, schedule, and price.",
    name: "Rogelio Tristan",
    role: "Primary Contact",
    seHabla: "Se Habla Español",
    callCta: "Call Rogelio",
    textCta: "Send a Text",
  },
  contact: {
    label: "Contact",
    heading: "Let's talk about your project.",
    sub: "Call, text, email, or send the bid form — whatever is easiest. We respond in English or Spanish.",
    call: "Call",
    text: "Text",
    email: "Email",
    bid: "Request a Bid",
    callSub: "281-541-8800",
    textSub: "Message us anytime",
    emailSub: "drywall3stan@gmail.com",
    bidSub: "Plans & details welcome",
    area: "Serving Greater Houston & surrounding areas — we come to your site.",
    forDrywall: "Drywall · Insulation · Sheetrock · Finishing · Texture · Painting · Residential · Commercial · Builders & Contractors",
    forR3: "Junk Removal · Property Cleanup · Concrete Breaking · Light Demolition · Dirt Delivery · Dirt Work · Tree Services · Trash Haul Away",
  },
  bid: {
    label: "Request a Bid",
    heading: "Tell us about the job.",
    sub: "Share the scope, attach plans if you have them, and we will come back with a straight number.",
    fullName: "Full Name *",
    company: "Company / Builder",
    email: "Email *",
    phone: "Phone *",
    address: "Project Address / Location",
    startDate: "Estimated Start Date",
    projectType: "Project Type",
    category: "Project Category",
    categories: {
      newConstruction: "New Construction",
      remodel: "Remodel",
      other: "Other",
    } as Record<string, string>,
    projectTypes: {
      residential: "Residential",
      commercial: "Commercial",
      newConstruction: "New Construction",
    } as Record<string, string>,
    servicesLegend: "Services Needed",
    drywallGroup: "Drywall & Construction",
    r3Group: "R3 Services",
    message: "Project Details",
    otherDescribe: "Please describe what you need",
    messagePh: "Location, square footage, timeline — anything that helps us price it right…",
    upload: "Plans / Blueprints",
    uploadHint: "PDF, JPG, PNG or WEBP — up to 5 files, 10MB each",
    uploadCta: "Tap to attach plans or photos",
    language: "Preferred Language",
    langEn: "English",
    langEs: "Español",
    submit: "Submit Bid Request",
    submitting: "Sending…",
    successTitle: "Request received.",
    contactDrywall: "Drywall & construction: Rogelio Tristan · 281-541-8800",
    contactR3: "R3 Services: Rogelio Tristan II · 281-780-0921",
    successText:
      "Thanks — we have your details and will follow up shortly. Need us sooner? Call 281-541-8800.",
    again: "Submit another request",
    error: "Something went wrong sending your request. Please try again or call us at 281-541-8800.",
  },
  companies: {
    label: "Companies We Work With",
    heading: "Companies We Work With",
    sub: "Real companies R3stan Drywall, Inc. has worked with — builders, contractors, and remodeling professionals.",
    types: {
      builder: "Builder",
      gc: "General Contractor",
      remodeling: "Remodeling",
      construction: "Construction Company",
      developer: "Developer",
      other: "Other",
    } as Record<string, string>,
  },
  serviceArea: {
    label: "Service Area",
    heading: "Serving Greater Houston & Beyond",
    text: "R3stan Drywall, Inc. and R3 Services serve job sites throughout Houston and the surrounding communities — and may travel to additional locations depending on the project.",
    note: "Don't see your city? We work throughout the Greater Houston area and surrounding communities. Contact us to see if we can service your project location.",
    disclaimer: "Service areas only — R3Stan does not have physical offices in these cities. We come to your job site.",
    cta: "Request a Bid",
  },
  faq: {
    label: "FAQ",
    heading: "Common questions, straight answers.",
    items: [
      {
        q: "Do you work with builders and general contractors?",
        a: "Yes. R3stan Drywall, Inc. works with builders, general contractors, and construction professionals on residential and commercial projects.",
      },
      {
        q: "Do you provide drywall for new construction?",
        a: "Yes. R3stan Drywall, Inc. provides drywall-related services for new residential and commercial construction.",
      },
      {
        q: "What drywall services do you provide?",
        a: "R3stan Drywall, Inc. provides insulation, sheetrock/drywall installation, finishing, texture, and painting services.",
      },
      {
        q: "Do you provide commercial services?",
        a: "Yes. Services are available for both residential and commercial projects.",
      },
      {
        q: "What services does R3 Services provide?",
        a: "R3 Services provides junk removal, property cleanup, concrete breaking, light demolition, dirt delivery, dirt work, tree services, and trash haul-away.",
      },
      {
        q: "Do you speak Spanish?",
        a: "Yes. English and Spanish are available.",
      },
      {
        q: "How do I request a bid?",
        a: "Customers, builders, and contractors can use the Request a Bid form to share project information and upload plans when applicable — or simply call or text us.",
      },
    ],
  },
  reviews: {
    label: "Reviews",
    heading: "What our clients say.",
    sub: "Genuine reviews from customers, builders, and contractors who have worked with R3stan Drywall, Inc. and R3 Services.",
    emptyTitle: "We're building our customer review page.",
    emptyText: "If you've worked with R3stan Drywall, Inc. or R3 Services, we'd appreciate hearing about your experience.",
    leaveReview: "Leave a Review",
    formTitle: "Worked With Us? Share Your Experience",
    formSub: "Your review goes live only after our team approves it.",
    name: "Name *",
    company: "Company Name (optional)",
    projectType: "Project Type",
    serviceUsed: "Service Used",
    brand: "Who did you work with?",
    rating: "Your Rating *",
    reviewText: "Written Review / Recommendation *",
    otherDescribe: "Please describe what you need",
    reviewPh: "Tell builders and customers what working with us was like…",
    city: "Project City (optional)",
    contact: "Email or Phone (optional — verification only, never published)",
    confirm: "I confirm that this review reflects my genuine experience working with R3stan Drywall, Inc. or R3 Services.",
    submit: "Submit Review",
    submitting: "Sending…",
    successTitle: "Thank you for your review.",
    successText: "Your review was received and is awaiting moderation. Once approved, it will appear on this page.",
    again: "Write another review",
    error: "Something went wrong sending your review. Please try again or call us at 281-541-8800.",
    confirmRequired: "Please confirm your review reflects your genuine experience.",
    googleTitle: "Prefer Google?",
    googleCta: "Leave Us a Google Review",
    featuredLabel: "Featured",
    projectTypes: {
      residential: "Residential",
      commercial: "Commercial",
      newConstruction: "New Construction",
      remodel: "Remodel",
      other: "Other",
    } as Record<string, string>,
    homeLabel: "Testimonials",
    homeHeading: "Straight from our clients.",
  },
  social: {
    comingSoon: "Social links coming soon",
  },
  footer: {
    tagline: "Drywall & construction done right — across Greater Houston.",
    servicesCol: "Drywall Services",
    r3Col: "R3 Services",
    navCol: "Navigate",
    contactCol: "Contact",
    areaCol: "Service Area",
    areaText: "Greater Houston, TX & surrounding communities",
    seHabla: "Se Habla Español",
    rights: "R3stan Drywall, Inc. All rights reserved.",
  },
};

export type Translations = typeof en;

const es: Translations = {
  brand: {
    name: "R3stan Drywall, Inc.",
    short: "R3STAN",
    sub: "DRYWALL, INC.",
    r3: "R3 Services",
  },
  nav: {
    services: "Servicios",
    work: "Nuestro Trabajo",
    builders: "Para Contratistas",
    r3: "R3 Services",
    about: "Nosotros",
    contact: "Contacto",
    requestBid: "Solicitar Cotización",
    stickyBid: "Cotizar",
    faq: "Preguntas",
    reviews: "Reseñas",
    menu: "Menú",
    close: "Cerrar",
  },
  hero: {
    eyebrow: "Contratista de Tablaroca · Gran Houston, TX",
    line1: "TABLAROCA Y",
    line2: "CONSTRUCCIÓN",
    line3: "BIEN HECHA.",
    tagline: "Residencial · Comercial · Construcción Nueva",
    sub: "Del aislamiento a la textura final — un solo equipo, un solo calendario, un solo estándar de acabado.",
    ctaBid: "Solicitar Cotización",
    ctaPlans: "Enviar Planos / Solicitar Cotización",
    ctaCall: "Llama al 281-541-8800",
    scroll: "Desliza para explorar",
  },
  marquee: [
    "Residencial",
    "Comercial",
    "Construcción Nueva",
    "Aislamiento",
    "Tablaroca",
    "Acabado",
    "Textura",
    "Pintura",
  ],
  builders: {
    label: "Para Contratistas y Constructores",
    heading: "Un socio de tablaroca que trabaja como parte de su propio equipo.",
    intro:
      "Trabajamos como los constructores lo necesitan: respuestas claras, calendarios honestos y acabados que pasan la inspección a la primera. Muéstrenos los planos — le mostraremos exactamente cómo los vamos a cumplir.",
    chapters: [
      {
        num: "01",
        title: "Comunicación",
        text: "Un solo contacto desde el estimado hasta la lista de pendientes. Siempre sabrá quién está en la obra, qué sigue y cuándo se termina — en inglés o español.",
      },
      {
        num: "02",
        title: "Programación",
        text: "Organizamos nuestros equipos alrededor de su calendario, coordinamos con los demás oficios y avisamos los conflictos con anticipación — antes de que le cuesten días.",
      },
      {
        num: "03",
        title: "Calidad",
        text: "Paredes derechas, acabados lisos, textura uniforme. Cada panel y cada capa con el mismo estándar, en cada trabajo, siempre.",
      },
    ],
    cta: "Iniciar una Conversación",
  },
  services: {
    label: "Lo Que Hacemos",
    heading: "Servicios de tablaroca, de principio a fin.",
    sub: "Desde el primer aislamiento hasta la última capa de pintura.",
    items: {
      insulation: {
        title: "Aislamiento",
        text: "Aislamiento bien instalado que prepara el camino para paredes cómodas y limpias.",
      },
      sheetrock: {
        title: "Instalación de Tablaroca / Sheetrock",
        text: "Instalación precisa, juntas parejas y fijación sólida en estructuras residenciales y comerciales.",
      },
      finishing: {
        title: "Acabado",
        text: "Cinta, pasta y lijado llevados al nivel que su proyecto requiere.",
      },
      texture: {
        title: "Textura",
        text: "Knockdown, piel de naranja, lisa — aplicada pareja en cada superficie.",
      },
      painting: {
        title: "Pintura",
        text: "Pintura interior limpia y completa para entregar el trabajo listo.",
      },
      residential: {
        title: "Construcción Residencial",
        text: "Casas unifamiliares, multifamiliares y remodelaciones — alcances completos de tablaroca para constructores.",
      },
      commercial: {
        title: "Construcción Comercial",
        text: "Remodelaciones de locales y construcciones comerciales entregadas a tiempo.",
      },
    },
  },
  work: {
    label: "Nuestro Trabajo",
    heading: "Proyectos y acabados.",
    sub: "Una muestra del estándar que llevamos a cada obra.",
    note: "Fotografía de muestra — fotos reales de proyectos reemplazarán estas imágenes.",
    all: "Todos",
    comingSoon: "Foto real del proyecto próximamente",
    cats: {
      sheetrock: "Tablaroca / Drywall",
      finishing: "Acabado",
      texture: "Textura",
      insulation: "Aislamiento",
      painting: "Pintura",
      residential: "Residencial",
      commercial: "Comercial",
      r3services: "R3 Services",
      dirtWork: "Trabajo de Tierra",
      cleanup: "Limpieza",
      demolition: "Demolición",
      hauling: "Acarreos",
    },
  },
  r3: {
    label: "Una División de R3stan",
    heading: "R3 Services",
    tagline: "Terrenos. Limpieza. Acarreos. Demolición Ligera.",
    sub: "El equipo hermano que prepara su terreno — y lo mantiene limpio.",
    items: {
      junk_removal: {
        title: "Remoción de Basura",
        text: "Sacamos escombros y basura de su propiedad, rápido.",
      },
      property_cleanup: {
        title: "Limpieza de Propiedades",
        text: "Limpieza completa de terrenos y propiedades, listas para el siguiente paso.",
      },
      concrete_breaking: {
        title: "Ruptura de Concreto",
        text: "Rompemos y removemos concreto viejo: banquetas, losas y más.",
      },
      light_demolition: {
        title: "Demolición Ligera",
        text: "Demoliciones interiores y estructuras ligeras, hechas con cuidado.",
      },
      dirt_delivery: {
        title: "Entrega de Tierra",
        text: "Tierra de relleno y material entregado directamente en su sitio.",
      },
      dirt_work: {
        title: "Trabajo de Tierra",
        text: "Nivelación y movimiento de tierra para dejar su proyecto listo.",
      },
      tree_services: {
        title: "Servicios de Árboles",
        text: "Poda y remoción de árboles para limpiar y abrir su terreno.",
      },
      trash_haul: {
        title: "Acarreo de Basura",
        text: "Acarreo confiable de basura y escombros fuera de su sitio.",
      },
    },
    cta: "Solicitar R3 Services",
    contactLabel: "Contacto de R3 Services",
    drywallNote: "Tablaroca y construcción: Rogelio Tristan · 281-541-8800",
    equipment: {
      label: "Equipo y Proyectos",
      note: "Imágenes de muestra — fotos reales de R3 Services se agregarán aquí.",
      comingSoon: "Foto próximamente",
      items: {
        bobcat: "Trabajo con Bobcat / Minicargadora",
        dirtWork: "Trabajo de Tierra",
        cleanup: "Limpieza de Propiedades",
        junk: "Remoción de Basura y Escombros",
        concrete: "Ruptura de Concreto",
        demolition: "Demolición Ligera",
        hauling: "Acarreos",
      } as Record<string, string>,
    },
  },
  about: {
    label: "Nosotros",
    heading: "Un equipo construido sobre respuestas claras y paredes derechas.",
    text: "R3stan Drywall, Inc. es un contratista de tablaroca y construcción del área de Houston que sirve a constructores, contratistas generales y propietarios en todo el Gran Houston. Cada proyecto pasa por una sola persona que contesta el teléfono.",
    text2:
      "Llame a Rogelio directamente — recibirá una respuesta clara sobre alcance, calendario y precio.",
    name: "Rogelio Tristan",
    role: "Contacto Principal",
    seHabla: "Se Habla Español",
    callCta: "Llamar a Rogelio",
    textCta: "Enviar un Mensaje",
  },
  contact: {
    label: "Contacto",
    heading: "Hablemos de su proyecto.",
    sub: "Llame, mande mensaje, escriba un correo o envíe la forma — lo que le sea más fácil. Respondemos en inglés o español.",
    call: "Llamar",
    text: "Mensaje",
    email: "Correo",
    bid: "Solicitar Cotización",
    callSub: "281-541-8800",
    textSub: "Escríbanos cuando quiera",
    emailSub: "drywall3stan@gmail.com",
    bidSub: "Planos y detalles bienvenidos",
    area: "Sirviendo el Gran Houston y alrededores — vamos hasta su obra.",
    forDrywall: "Tablaroca · Aislamiento · Sheetrock · Acabado · Textura · Pintura · Residencial · Comercial · Constructores y Contratistas",
    forR3: "Remoción de Basura · Limpieza de Propiedades · Ruptura de Concreto · Demolición Ligera · Entrega de Tierra · Trabajo de Tierra · Servicios de Árboles · Acarreo de Basura",
  },
  bid: {
    label: "Solicitar Cotización",
    heading: "Cuéntenos del trabajo.",
    sub: "Comparta el alcance, adjunte planos si los tiene, y le responderemos con un número claro.",
    fullName: "Nombre Completo *",
    company: "Compañía / Constructor",
    email: "Correo Electrónico *",
    phone: "Teléfono *",
    address: "Dirección / Ubicación del Proyecto",
    startDate: "Fecha Estimada de Inicio",
    projectType: "Tipo de Proyecto",
    category: "Categoría del Proyecto",
    categories: {
      newConstruction: "Construcción Nueva",
      remodel: "Remodelación",
      other: "Otro",
    } as Record<string, string>,
    projectTypes: {
      residential: "Residencial",
      commercial: "Comercial",
      newConstruction: "Construcción Nueva",
    },
    servicesLegend: "Servicios Necesarios",
    drywallGroup: "Tablaroca y Construcción",
    r3Group: "R3 Services",
    message: "Detalles del Proyecto",
    otherDescribe: "Por favor describa lo que necesita",
    messagePh: "Ubicación, pies cuadrados, calendario — cualquier cosa que nos ayude a cotizar bien…",
    upload: "Planos / Blueprints",
    uploadHint: "PDF, JPG, PNG o WEBP — hasta 5 archivos, 10MB cada uno",
    uploadCta: "Toque para adjuntar planos o fotos",
    language: "Idioma Preferido",
    langEn: "English",
    langEs: "Español",
    submit: "Enviar Solicitud",
    submitting: "Enviando…",
    successTitle: "Solicitud recibida.",
    contactDrywall: "Tablaroca y construcción: Rogelio Tristan · 281-541-8800",
    contactR3: "R3 Services: Rogelio Tristan II · 281-780-0921",
    successText:
      "Gracias — ya tenemos sus datos y le contactaremos pronto. ¿Nos necesita antes? Llame al 281-541-8800.",
    again: "Enviar otra solicitud",
    error: "Hubo un problema al enviar su solicitud. Inténtelo de nuevo o llámenos al 281-541-8800.",
  },
  companies: {
    label: "Empresas con las que Trabajamos",
    heading: "Empresas con las que Trabajamos",
    sub: "Empresas reales con las que R3stan Drywall, Inc. ha trabajado — constructores, contratistas y profesionales de remodelación.",
    types: {
      builder: "Constructor",
      gc: "Contratista General",
      remodeling: "Remodelación",
      construction: "Compañía de Construcción",
      developer: "Desarrollador",
      other: "Otro",
    } as Record<string, string>,
  },
  serviceArea: {
    label: "Área de Servicio",
    heading: "Sirviendo el Área Metropolitana de Houston y Sus Alrededores",
    text: "R3stan Drywall, Inc. y R3 Services atienden obras en todo Houston y las comunidades alrededor — y podemos viajar a ubicaciones adicionales según el proyecto.",
    note: "¿No ve su ciudad? Trabajamos en toda el área metropolitana de Houston y comunidades circundantes. Contáctenos para confirmar si podemos atender su proyecto.",
    disclaimer: "Solo áreas de servicio — R3Stan no tiene oficinas físicas en estas ciudades. Vamos hasta su obra.",
    cta: "Solicitar Cotización",
  },
  faq: {
    label: "Preguntas Frecuentes",
    heading: "Preguntas comunes, respuestas claras.",
    items: [
      {
        q: "¿Trabajan con constructores y contratistas generales?",
        a: "Sí. R3stan Drywall, Inc. trabaja con constructores, contratistas generales y profesionales de la construcción en proyectos residenciales y comerciales.",
      },
      {
        q: "¿Proveen tablaroca para construcción nueva?",
        a: "Sí. R3stan Drywall, Inc. provee servicios de tablaroca para construcción nueva residencial y comercial.",
      },
      {
        q: "¿Qué servicios de tablaroca ofrecen?",
        a: "R3stan Drywall, Inc. ofrece aislamiento, instalación de tablaroca/sheetrock, acabado, textura y pintura.",
      },
      {
        q: "¿Ofrecen servicios comerciales?",
        a: "Sí. Los servicios están disponibles para proyectos residenciales y comerciales.",
      },
      {
        q: "¿Qué servicios ofrece R3 Services?",
        a: "R3 Services ofrece remoción de basura, limpieza de propiedades, ruptura de concreto, demolición ligera, entrega de tierra, trabajo de tierra, servicios de árboles y acarreo de basura.",
      },
      {
        q: "¿Hablan español?",
        a: "Sí. Inglés y español disponibles.",
      },
      {
        q: "¿Cómo solicito una cotización?",
        a: "Clientes, constructores y contratistas pueden usar la forma de Solicitar Cotización para compartir la información de su proyecto y subir planos cuando aplique — o simplemente llamar o mandar un mensaje de texto.",
      },
    ],
  },
  reviews: {
    label: "Reseñas",
    heading: "Lo que dicen nuestros clientes.",
    sub: "Reseñas genuinas de clientes, constructores y contratistas que han trabajado con R3stan Drywall, Inc. y R3 Services.",
    emptyTitle: "Estamos construyendo nuestra página de reseñas.",
    emptyText: "Si ha trabajado con R3stan Drywall, Inc. o R3 Services, agradeceríamos conocer su experiencia.",
    leaveReview: "Dejar una Reseña",
    formTitle: "¿Trabajó Con Nosotros? Comparta Su Experiencia",
    formSub: "Su reseña se publica solo después de que nuestro equipo la apruebe.",
    name: "Nombre *",
    company: "Nombre de la Compañía (opcional)",
    projectType: "Tipo de Proyecto",
    serviceUsed: "Servicio Utilizado",
    brand: "¿Con quién trabajó?",
    rating: "Su Calificación *",
    reviewText: "Reseña / Recomendación Escrita *",
    otherDescribe: "Por favor describa lo que necesita",
    reviewPh: "Cuente a constructores y clientes cómo fue trabajar con nosotros…",
    city: "Ciudad del Proyecto (opcional)",
    contact: "Correo o Teléfono (opcional — solo para verificación, nunca se publica)",
    confirm: "Confirmo que esta reseña refleja mi experiencia genuina trabajando con R3stan Drywall, Inc. o R3 Services.",
    submit: "Enviar Reseña",
    submitting: "Enviando…",
    successTitle: "Gracias por su reseña.",
    successText: "Su reseña fue recibida y está esperando moderación. Una vez aprobada, aparecerá en esta página.",
    again: "Escribir otra reseña",
    error: "Hubo un problema al enviar su reseña. Inténtelo de nuevo o llámenos al 281-541-8800.",
    confirmRequired: "Por favor confirme que su reseña refleja su experiencia genuina.",
    googleTitle: "¿Prefiere Google?",
    googleCta: "Déjenos una Reseña en Google",
    featuredLabel: "Destacada",
    projectTypes: {
      residential: "Residencial",
      commercial: "Comercial",
      newConstruction: "Construcción Nueva",
      remodel: "Remodelación",
      other: "Otro",
    } as Record<string, string>,
    homeLabel: "Testimonios",
    homeHeading: "Directo de nuestros clientes.",
  },
  social: {
    comingSoon: "Enlaces sociales próximamente",
  },
  footer: {
    tagline: "Tablaroca y construcción bien hecha — en todo el Gran Houston.",
    servicesCol: "Servicios de Tablaroca",
    r3Col: "R3 Services",
    navCol: "Navegar",
    contactCol: "Contacto",
    areaCol: "Área de Servicio",
    areaText: "Gran Houston, TX y comunidades alrededor",
    seHabla: "Se Habla Español",
    rights: "R3stan Drywall, Inc. Todos los derechos reservados.",
  },
};

export const translations: Record<Lang, Translations> = { en, es };
